import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler, Legend } from "chart.js";
import { ClipboardList, Send, PenLine, X, AlertTriangle, TrendingDown, TrendingUp, Minus, ShieldAlert } from "lucide-react";
import api from "../../api";
import RiskAssessmentSection, { emptyRisk, riskStatus } from "../consultation/RiskAssessmentSection";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler, Legend);

// Measures tab on the patient file (backend clinical/monitoring/measures_routes.py):
// questionnaires the patient fills in from their app, or the clinician fills
// in during a session, with a score trend per questionnaire. Handles
// per-question answer choices (AUDIT, EPDS), skip logic (C-SSRS), subscales
// (DASS-21) and "higher is better" scores (WHO-5).
const TONE_STYLE = {
  ok: "bg-emerald-100 text-emerald-800",
  mild: "bg-lime-100 text-lime-800",
  moderate: "bg-amber-100 text-amber-800",
  severe: "bg-rose-100 text-rose-700",
};
const LEGACY_TONE = { minimal: "ok", mild: "mild", moderate: "moderate", moderately_severe: "severe", severe: "severe" };
const fmt = (iso) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
const days = (n) => `${n} day${Number(n) === 1 ? "" : "s"}`;
const bandOf = (bands, sev) => (bands || []).find((b) => b.severity === sev);
const toneClass = (bands, sev) => TONE_STYLE[bandOf(bands, sev)?.tone || LEGACY_TONE[sev]] || "bg-secondary-100 text-secondary-700";
const optionsFor = (def, item) => item.options || def.options || [];
const matches = (c, answers) => Number(answers[c.item]) === Number(c.equals);
// Skip logic: show_if {item, equals} or {any: [...]} (ASQ question 5 after any "yes")
const isVisible = (item, answers) => !item.show_if || (item.show_if.any ? item.show_if.any.some((c) => matches(c, answers)) : matches(item.show_if, answers));

function Trend({ m }) {
  const labels = m.responses.map((r) => new Date(r.completed_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }));
  const colours = ["#4f46e5", "#0d9488", "#db2777"];
  const datasets = m.scoring === "subscales"
    ? m.subscales.map((s, i) => ({ label: s.label, data: m.responses.map((r) => r.subscores?.[s.key]?.score ?? null), borderColor: colours[i % 3], tension: 0.25, pointRadius: 3 }))
    : [{ label: m.short_name, data: m.responses.map((r) => r.total_score), borderColor: "#4f46e5", backgroundColor: "rgba(79,70,229,0.08)", fill: true, tension: 0.25, pointRadius: 4 }];
  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: m.scoring === "subscales", position: "bottom" } },
    scales: { y: { min: 0, ...(m.scoring === "subscales" ? {} : { max: m.max_score || undefined }) } },
  };
  return <div className="h-40"><Line data={{ labels, datasets }} options={options} /></div>;
}

function Administer({ def, onCancel, onDone, patientId }) {
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const shown = def.items.filter((i) => isVisible(i, answers));
  const done = shown.every((i) => answers[i.id] !== undefined);
  const answer = (item, value) => {
    const next = { ...answers, [item.id]: value };
    // drop answers to questions that are now skipped
    def.items.forEach((i) => { if (!isVisible(i, next)) delete next[i.id]; });
    setAnswers(next);
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(answers).filter(([k]) => shown.some((i) => i.id === k)));
      const r = await api.enterMeasureResponse(patientId, { code: def.code, answers: payload });
      toast.success(`${def.short_name} saved`);
      onDone(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="rounded-xl bg-white border border-primary-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold text-secondary-900">{def.name} - filling in with the patient</p>
        <button type="button" onClick={onCancel} aria-label="Close" className="p-1 text-secondary-400 hover:text-secondary-700"><X className="w-4 h-4" /></button>
      </div>
      <p className="text-sm text-secondary-600">{def.intro}</p>
      <ol className="space-y-3">
        {shown.map((item, idx) => (
          <li key={item.id}>
            <p className="text-sm text-secondary-800 mb-1.5">{idx + 1}. {item.text}</p>
            <div className="flex flex-wrap gap-1.5">
              {optionsFor(def, item).map((o) => (
                <button key={o.value} type="button" onClick={() => answer(item, o.value)}
                  className={`rounded-lg border px-2.5 py-1 text-xs font-medium ${answers[item.id] === o.value ? "bg-primary-600 border-primary-600 text-white" : "bg-white border-secondary-300 text-secondary-700 hover:border-primary-400"}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </li>
        ))}
      </ol>
      <div className="flex items-center gap-3">
        <button type="button" onClick={save} disabled={!done || saving} className="rounded-lg bg-primary-600 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">{saving ? "Saving…" : "Save answers"}</button>
        <span className="text-xs text-secondary-500">{shown.filter((i) => answers[i.id] !== undefined).length} of {shown.length} answered</span>
      </div>
    </div>
  );
}

function Answers({ def, response }) {
  if (!def) return <p className="text-xs text-secondary-500">The questions for this result aren't available.</p>;
  const critical = new Set((response.critical_flags || []).map((f) => String(f.item)));
  const asked = def.items.filter((i) => response.answers?.[i.id] !== undefined);
  return (
    <ol className="mt-2 space-y-1.5 rounded-lg bg-secondary-50 p-3">
      {asked.map((item, idx) => {
        const v = response.answers[item.id];
        const label = optionsFor(def, item).find((o) => o.value === v)?.label ?? v;
        const crit = critical.has(String(item.id));
        return (
          <li key={item.id} className={`flex gap-3 text-xs ${crit ? "rounded-md bg-rose-50 border border-rose-200 p-1.5" : ""}`}>
            <span className="flex-1 text-secondary-700">{idx + 1}. {item.text}</span>
            <span className={`shrink-0 font-semibold ${crit ? "text-rose-700" : "text-secondary-900"}`}>{label} <span className="font-normal text-secondary-400">({v})</span></span>
          </li>
        );
      })}
    </ol>
  );
}

// After a suicide-risk screener (ASQ, C-SSRS) in session: what to do now, and the
// suggested risk assessment for the clinician to check and save.
function ScreenToRisk({ patientId, suggestion, source, action, onClose }) {
  const [value, setValue] = useState({ ...emptyRisk(), ...suggestion, harm_to_others: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.recordPatientRisk(patientId, { ...value, source });
      toast.success("Risk assessment saved");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save the assessment.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="rounded-xl bg-white border border-rose-200 p-4 space-y-3">
      <p className="flex items-center gap-2 text-sm font-bold text-secondary-900"><ShieldAlert className="w-4 h-4 text-rose-600" /> Record a risk assessment from this {source === "asq" ? "ASQ" : "C-SSRS"}</p>
      {action && <p className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900"><span className="font-semibold">What to do now:</span> {action}</p>}
      <p className="text-xs text-secondary-600">Pre-filled from the answers - check every field, add risk to others, protective factors and the action taken. You decide.</p>
      <RiskAssessmentSection value={value} onChange={setValue} />
      <div className="flex gap-2">
        <button type="button" onClick={save} disabled={saving || riskStatus(value) !== "complete"} className="rounded-lg bg-primary-600 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">{saving ? "Saving…" : "Save risk assessment"}</button>
        <button type="button" onClick={onClose} className="rounded-lg border border-secondary-300 text-sm px-4 py-2">Not now</button>
      </div>
    </div>
  );
}

export default function MeasuresPanel({ patientId }) {
  const [data, setData] = useState(null);
  const [defs, setDefs] = useState([]);
  const [assign, setAssign] = useState({ code: "", schedule: "once", interval_days: 14 });
  const [administer, setAdminister] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openAnswers, setOpenAnswers] = useState(null); // response id
  const [riskSuggestion, setRiskSuggestion] = useState(null);

  const load = () => api.getPatientMeasures(patientId).then((r) => setData(r.data)).catch(() => setData({ measures: [], assignments: [] }));
  useEffect(() => {
    load();
    api.getMeasureDefinitions().then((r) => { setDefs(r.data || []); setAssign((a) => ({ ...a, code: a.code || r.data?.[0]?.code || "" })); }).catch(() => {});
  }, [patientId]); // eslint-disable-line

  const sendToPatient = async () => {
    setBusy(true);
    try {
      await api.assignMeasure(patientId, { code: assign.code, schedule: assign.schedule, interval_days: assign.schedule === "every_n_days" ? Number(assign.interval_days) : undefined });
      toast.success(assign.schedule === "every_n_days" ? `Sent - it will repeat every ${days(assign.interval_days)}` : "Sent to the patient's app");
      setAssign((a) => ({ ...a, schedule: "once" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't send it.");
    } finally {
      setBusy(false);
    }
  };
  const cancel = async (a) => {
    try { await api.cancelMeasureAssignment(a.id); toast.success(a.schedule === "every_n_days" ? "Repeats stopped" : "Cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.error || "Couldn't cancel."); }
  };

  if (!data) return <div className="rounded-xl bg-white border border-secondary-100 p-4 text-sm text-secondary-500">Loading…</div>;
  const def = defs.find((d) => d.code === assign.code);
  const defByCode = Object.fromEntries(defs.map((d) => [d.code, d]));
  const pendingByCode = Object.fromEntries(data.assignments.filter((a) => a.status === "pending").map((a) => [a.code, a]));
  const clinicianOnly = def && def.administered_by !== "patient";

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-white border border-secondary-100 p-4 space-y-2">
        <p className="text-sm font-semibold text-secondary-800">Give a questionnaire</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="normal-case font-sans tracking-normal text-sm">
            <span className="block text-xs text-secondary-600 mb-1">Questionnaire</span>
            <select value={assign.code} onChange={(e) => setAssign({ ...assign, code: e.target.value })} className="rounded-md border border-secondary-300 p-2 text-sm min-w-[220px]">
              {defs.map((d) => <option key={d.code} value={d.code}>{d.name}{d.administered_by !== "patient" ? " - asked by clinician" : ""}</option>)}
            </select>
          </label>
          {!clinicianOnly && (
            <label className="normal-case font-sans tracking-normal text-sm">
              <span className="block text-xs text-secondary-600 mb-1">How often</span>
              <select value={assign.schedule} onChange={(e) => setAssign({ ...assign, schedule: e.target.value })} className="rounded-md border border-secondary-300 p-2 text-sm">
                <option value="once">Once</option>
                <option value="every_n_days">Repeat</option>
              </select>
            </label>
          )}
          {!clinicianOnly && assign.schedule === "every_n_days" && (
            <label className="normal-case font-sans tracking-normal text-sm">
              <span className="block text-xs text-secondary-600 mb-1">Every</span>
              <span className="flex items-center gap-1.5">
                <input type="number" min={1} max={90} value={assign.interval_days} onChange={(e) => setAssign({ ...assign, interval_days: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()} className="w-16 rounded-md border border-secondary-300 p-2 text-sm" /> days
              </span>
            </label>
          )}
          {!clinicianOnly && (
            <button type="button" onClick={sendToPatient} disabled={!assign.code || busy || Boolean(pendingByCode[assign.code])}
              title={pendingByCode[assign.code] ? "Already waiting for the patient - see its card below" : undefined}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold px-3 py-2 disabled:opacity-50">
              <Send className="w-4 h-4" /> Send to patient's app
            </button>
          )}
          <button type="button" onClick={() => def && setAdminister(def)} disabled={!def} className={`inline-flex items-center gap-1.5 rounded-lg text-sm font-semibold px-3 py-2 ${clinicianOnly ? "bg-primary-600 text-white" : "border border-secondary-300"}`}>
            <PenLine className="w-4 h-4" /> Fill in now (in session)
          </button>
        </div>
        {def?.description && <p className="text-xs text-secondary-500">{def.description}</p>}
      </div>

      {administer && <Administer def={administer} patientId={patientId} onCancel={() => setAdminister(null)}
        onDone={(resp) => { setAdminister(null); if (resp?.risk_suggestion) setRiskSuggestion(resp); load(); }} />}
      {riskSuggestion && <ScreenToRisk patientId={patientId} suggestion={riskSuggestion.risk_suggestion} source={riskSuggestion.risk_source}
        action={riskSuggestion.action} onClose={() => setRiskSuggestion(null)} />}

      {data.measures.length === 0 && (
        <p className="rounded-xl bg-white border border-secondary-100 p-6 text-sm text-secondary-500">No questionnaires yet. Send one to the patient's app, or fill one in together in a session.</p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        {data.measures.map((m) => {
          const pend = pendingByCode[m.code];
          const diff = m.latest && m.first ? m.latest.total_score - m.first.total_score : 0;
          const better = m.higher_is_better ? diff > 0 : diff < 0;
          const worse = m.higher_is_better ? diff < 0 : diff > 0;
          const Arrow = diff < 0 ? TrendingDown : diff > 0 ? TrendingUp : Minus;
          return (
            <div key={m.code} className="rounded-xl bg-white border border-secondary-100 p-4 space-y-3 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <ClipboardList className="w-5 h-5 text-primary-600" />
                <h3 className="text-base font-bold text-secondary-900">{m.name}</h3>
                {m.latest && m.scoring !== "subscales" && <>
                  <span className="text-2xl font-bold text-secondary-900 ml-1">{m.latest.total_score}<span className="text-sm font-normal text-secondary-400">/{m.max_score}</span></span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${toneClass(m.bands, m.latest.severity)}`}>{bandOf(m.bands, m.latest.severity)?.label || m.latest.severity}</span>
                </>}
                {m.responses.length > 1 && m.scoring !== "subscales" && m.scoring !== "rules" && (
                  <span title={`Change since the first result (${m.higher_is_better ? "higher" : "lower"} is better)`} className={`inline-flex items-center gap-1 text-xs font-semibold ${better ? "text-emerald-700" : worse ? "text-rose-700" : "text-secondary-500"}`}>
                    <Arrow className="w-4 h-4" /> {diff > 0 ? "+" : ""}{diff} since {fmt(m.first.completed_at)}
                  </span>
                )}
              </div>
              {m.latest && m.scoring === "subscales" && (
                <div className="flex flex-wrap gap-2">
                  {m.subscales.map((s) => {
                    const sc = m.latest.subscores?.[s.key];
                    return sc ? (
                      <span key={s.key} className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${toneClass(s.bands, sc.severity)}`}>{s.label} {sc.score} · {sc.band_label}</span>
                    ) : null;
                  })}
                </div>
              )}

              {pend && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 text-xs text-sky-900">
                  <span className="font-semibold">{pend.schedule === "every_n_days" ? `Repeats every ${days(pend.interval_days)}` : "Sent once"}</span>
                  <span>· {new Date(pend.due_at) > new Date() ? `next opens ${fmt(pend.due_at)}` : `waiting for the patient since ${fmt(pend.due_at)}`}</span>
                  <button type="button" onClick={() => cancel(pend)} className="ml-auto font-semibold text-sky-800 hover:text-rose-600">{pend.schedule === "every_n_days" ? "Stop repeating" : "Cancel"}</button>
                </div>
              )}

              {m.responses.length > 1 && m.scoring !== "rules" && <Trend m={m} />}
              {m.responses.length === 0 && <p className="text-sm text-secondary-500">No answers yet.</p>}
              <ul className="divide-y divide-secondary-100 text-sm">
                {[...m.responses].reverse().map((r) => (
                  <li key={r.id} className="py-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="w-24 text-secondary-600">{fmt(r.completed_at)}</span>
                      {m.scoring === "subscales" ? (
                        <span className="text-xs text-secondary-700">{m.subscales.map((s) => `${s.label[0]} ${r.subscores?.[s.key]?.score ?? "-"}`).join(" · ")}</span>
                      ) : (<>
                        {m.scoring !== "rules" && <span className="font-semibold text-secondary-900">{r.total_score}</span>}
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${toneClass(m.bands, r.severity)}`}>{bandOf(m.bands, r.severity)?.label || r.severity}</span>
                      </>)}
                      <span className="text-xs text-secondary-500">{r.completed_by === "patient" ? `by the patient${r.language && r.language !== "en" ? ` (${r.language})` : ""}` : `in session${r.entered_by_name ? ` with ${r.entered_by_name}` : ""}`}</span>
                      {r.critical_flags.length > 0 && (
                        <span title={r.critical_flags.map((f) => f.reason).join("; ")} className="inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 px-2 py-0.5 text-[11px] font-semibold text-rose-700 cursor-help">
                          <AlertTriangle className="w-3 h-3" /> Needs risk review
                        </span>
                      )}
                      <button type="button" onClick={() => setOpenAnswers(openAnswers === r.id ? null : r.id)} className="ml-auto text-xs font-semibold text-primary-700 hover:underline">
                        {openAnswers === r.id ? "Hide answers" : "View answers"}
                      </button>
                    </div>
                    {openAnswers === r.id && <Answers def={defByCode[m.code]} response={r} />}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
